import { Button } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { TextInput } from '@/components/Form/TextInput';
import { ActionBar } from '@/components/Layout';

import { CURRENCY_SIGN } from '@/constants/countries';
import { AMOUNT_PATTERN } from '@/constants/patterns';

import { useAppSelector } from '@/hooks/redux';
import { useQuestionInfoMutation } from '@/hooks/useQuestionInfoMutation';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';
import { selectUserData } from '@/redux/user-data';

import { getDataTestId } from '@/utils/browser';
import { getUserCurrency, getUserMonthlyDepositCurrency } from '@/utils/user';

import { QuestionAboutMultipleMonthlyDeposit } from './QuestionAboutMultipleMonthlyDeposit';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

export function QuestionAboutMonthlyDeposit({
  onAnswered,
  question,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user, accountTypeById } = useAppSelector(selectUserData);
  const { defaultCurrency, isMultipleMonthlyDeposits } =
    useAppSelector(selectConfigsState);

  const getUserCurrencyValue = () => {
    if (!user) return undefined;
    if (isMultipleMonthlyDeposits) {
      return getUserCurrency(user, { accountTypeById, defaultCurrency });
    }
    return getUserMonthlyDepositCurrency(user, {
      accountTypeById,
      defaultCurrency,
    });
  };
  const userCurrency = getUserCurrencyValue();
  const questionKey = question.extraProps?.questionKey as string | undefined;
  const displayQuestion = questionKey
    ? t(questionKey, {
        currency: userCurrency
          ? CURRENCY_SIGN[userCurrency] || userCurrency
          : '',
      })
    : question.question;
  const {
    handleSubmit,
    control,
    formState: { errors: isValid },
  } = useForm<{
    monthlyDeposit: string;
  }>({
    defaultValues: {
      monthlyDeposit: '',
    },
    mode: 'onChange',
  });

  const { mutate, isPending, error } = useQuestionInfoMutation({
    onFinished: onAnswered,
    target: props.pepAnswerTarget,
  });

  const onSubmit = handleSubmit(async payload => {
    if (!user) return;

    const userCurrency = isMultipleMonthlyDeposits
      ? getUserCurrency(user, { accountTypeById, defaultCurrency })
      : getUserMonthlyDepositCurrency(user, {
          accountTypeById,
          defaultCurrency,
        });

    mutate({
      monthlyDeposit: Number.parseFloat(payload.monthlyDeposit),
      ...(userCurrency ? { monthlyDepositCurrency: userCurrency } : {}),
    });
  });

  if (isMultipleMonthlyDeposits) {
    return (
      <QuestionAboutMultipleMonthlyDeposit
        onAnswered={onAnswered}
        question={{ ...question, question: displayQuestion }}
        {...props}
      />
    );
  }

  return (
    <QuestionForm
      {...props}
      question={{ ...question, question: displayQuestion }}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <Controller
        control={control}
        name="monthlyDeposit"
        rules={{
          required: t('questionDrawer.monthlyDeposit.monthlyDeposit.required'),
          pattern: {
            value: new RegExp(AMOUNT_PATTERN),
            message: t('questionDrawer.monthlyDeposit.monthlyDeposit.pattern'),
          },
        }}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { error },
        }) => (
          <TextInput
            type="text"
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            error={!!error}
            helperText={error?.message}
            placeholder={t(
              'questionDrawer.monthlyDeposit.monthlyDeposit.placeHolder',
            )}
            inputMode="numeric"
            autoComplete="off"
            disabled={isPending}
          />
        )}
      />
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-proceed-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.monthlyDeposit.button.proceed')}
        </Button>
      </ActionBar>
      <FormErrorLogger
        formName="QuestionAboutMonthlyDeposit"
        control={control}
      />
    </QuestionForm>
  );
}
